import { type PageViewport } from "pdfjs-dist";

import { PDFDocumentData } from "@/src/components/pdf-viewer/types";
import { rotateNormalizedBoundingBox } from "@/src/lib/pdf-page-geometry";
import { BoundingBox, type ExtractionMetadata } from "@/src/models/section";

export interface BoundingBoxStyleOptions {
  borderWidth?: number;
  focusedBorderWidth?: number;
  color?: string;
  focusedColor?: string;
}

export function clearHighlightCanvases(documentData: PDFDocumentData[]) {
  const canvases = documentData.map((data) => data.highlightRef.current).filter(Boolean);

  canvases.map(clearHighlightCanvas);
}

export function clearHighlightCanvas(canvas: HTMLCanvasElement | null) {
  if (!canvas) {
    return;
  }

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

export function drawMetadata({
  documentData,
  metadata,
  padding = 10,
  style,
  focusedIndex = null,
  hideNonFocusedBoxes = false,
}: {
  documentData: PDFDocumentData[];
  metadata: ExtractionMetadata;
  padding?: number;
  style?: BoundingBoxStyleOptions;
  focusedIndex?: number | null;
  hideNonFocusedBoxes?: boolean;
}) {
  clearHighlightCanvases(documentData);

  // Check if bboxes exists and is an array before iterating
  if (!metadata.bboxes || !Array.isArray(metadata.bboxes)) {
    return;
  }

  metadata.bboxes.forEach((boundingBox, index) => {
    // If hideNonFocusedBoxes is true, only draw the focused box
    if (hideNonFocusedBoxes && index !== focusedIndex) {
      return;
    }

    const canvas = documentData[boundingBox.page]?.highlightRef?.current;
    const viewport = documentData[boundingBox.page]?.viewport;

    if (!canvas || !viewport) {
      return;
    }

    drawBoundingBox({
      canvas,
      boundingBox,
      viewport,
      padding,
      style,
      isFocused: index === focusedIndex,
    });
  });
}

export function drawBoundingBox({
  canvas,
  boundingBox,
  viewport,
  padding = 10,
  style,
  isFocused = false,
}: {
  canvas: HTMLCanvasElement;
  boundingBox: BoundingBox;
  viewport: PageViewport;
  padding?: number;
  style?: BoundingBoxStyleOptions;
  isFocused?: boolean;
}) {
  const ctx = canvas.getContext("2d");

  if (!ctx) return;
  const [x, y, width, height] = boundingBox.bbox.xywh;
  const [viewportX, viewportY, viewportBoxWidth, viewportBoxHeight] = rotateNormalizedBoundingBox(
    [x, y, width, height],
    viewport.rotation
  );
  const scaledX = viewportX * viewport.width;
  const scaledY = viewportY * viewport.height;
  const pageWidth = viewportBoxWidth * viewport.width;
  const pageHeight = viewportBoxHeight * viewport.height;

  const effectiveBorderWidth = isFocused
    ? (style?.focusedBorderWidth ?? style?.borderWidth ?? 8)
    : (style?.borderWidth ?? 8);

  const effectiveColor = isFocused
    ? (style?.focusedColor ?? "rgba(57, 255, 20, 0.8)")
    : (style?.color ?? "rgba(57, 255, 20, 0.8)");

  // Set stroke style for the border
  ctx.strokeStyle = effectiveColor;

  ctx.lineWidth = effectiveBorderWidth;

  // Draw only the stroke with the padding applied
  ctx.strokeRect(scaledX - padding, scaledY - padding, pageWidth + padding * 2, pageHeight + padding * 2);
}
